import type { Locale } from '../index'

/**
 * Page-body copy for the marketing "Get a quote" page, in all three locales
 * (en / zh-Hant / zh-Hans). zh-Hant and zh-Hans are distinct, hand-written
 * translations — never auto-converted from one to the other.
 */
export const quote: Record<Locale, Record<string, string>> = {
  en: {
    'quote.head.title': 'Get a quote — Geo4Logistics',
    'quote.head.description':
      'Request a sea or air freight quote from Geo4Logistics. Tell us the mode, origin, destination and cargo — we reply with itemised pricing and a transit time, usually within 1–2 business days.',

    'quote.h1': 'Get a quote',
    'quote.intro':
      'Tell us the mode, route and cargo. We come back with itemised pricing and a transit time — usually within 1–2 business days.',

    'quote.mode.legend': 'Shipping mode',
    'quote.mode.oceanFcl': 'Ocean FCL',
    'quote.mode.oceanLcl': 'Ocean LCL',
    'quote.mode.air': 'Air freight',
    'quote.mode.rail': 'Rail freight',
    'quote.mode.road': 'Road & customs',
    'quote.mode.notSure': 'Not sure',

    'quote.field.origin': 'Origin *',
    'quote.field.origin.placeholder': 'City or port, country',
    'quote.field.destination': 'Destination *',
    'quote.field.destination.placeholder': 'City or port, country',
    'quote.field.name': 'Full name *',
    'quote.field.name.placeholder': 'Your name',
    'quote.field.company': 'Company *',
    'quote.field.company.placeholder': 'Company name',
    'quote.field.email': 'Work email *',
    'quote.field.email.placeholder': 'you@company.com',
    'quote.field.phone': 'Phone',
    'quote.field.phone.placeholder': '+1 / +886 …',
    'quote.field.details': 'Shipment details',
    'quote.field.details.placeholder':
      'What are you shipping? Rough weight or volume, ready date, deadlines, anything else we should know…',

    'quote.submit': 'Request quote',
    'quote.reply_note': 'We typically reply within 1–2 business days, in English or Chinese.',
    'quote.error': 'Please check your details and try again.',
    'quote.success':
      'Thanks — your request has been received. Our team will review it and get back to you shortly.',
    'quote.modal.title': 'Quote requested',
    'quote.modal.ok': 'OK',

    'quote.contact.pre': 'Just need to reach us?',
    'quote.contact.link': 'Contact the team',
    'quote.contact.post': 'instead.',
  },
  'zh-Hant': {
    'quote.head.title': '取得報價 — Geo4Logistics',
    'quote.head.description':
      '向 Geo4Logistics 索取海運或空運報價。請告訴我們運輸方式、起運地、目的地與貨物 — 我們將回覆逐項列明的價格與運輸時間，通常於一至兩個工作天內回覆。',

    'quote.h1': '取得報價',
    'quote.intro':
      '請告訴我們運輸方式、路線與貨物。我們將回覆逐項列明的價格與運輸時間 — 通常於一至兩個工作天內回覆。',

    'quote.mode.legend': '運輸方式',
    'quote.mode.oceanFcl': '海運整櫃（FCL）',
    'quote.mode.oceanLcl': '海運併櫃（LCL）',
    'quote.mode.air': '空運',
    'quote.mode.rail': '鐵路運輸',
    'quote.mode.road': '陸運與報關',
    'quote.mode.notSure': '尚未確定',

    'quote.field.origin': '起運地 *',
    'quote.field.origin.placeholder': '城市或港口、國家',
    'quote.field.destination': '目的地 *',
    'quote.field.destination.placeholder': '城市或港口、國家',
    'quote.field.name': '姓名 *',
    'quote.field.name.placeholder': '您的姓名',
    'quote.field.company': '公司 *',
    'quote.field.company.placeholder': '公司名稱',
    'quote.field.email': '公司電子郵件 *',
    'quote.field.email.placeholder': 'you@company.com',
    'quote.field.phone': '電話',
    'quote.field.phone.placeholder': '+1 / +886 …',
    'quote.field.details': '貨件明細',
    'quote.field.details.placeholder':
      '您要運送什麼貨物？大致重量或材積、備貨日期、期限，以及任何我們需要了解的資訊…',

    'quote.submit': '送出報價需求',
    'quote.reply_note': '我們通常於一至兩個工作天內以英文或中文回覆。',
    'quote.error': '請檢查您填寫的資料後再試一次。',
    'quote.success': '感謝您！我們已收到您的需求，團隊將盡快審閱並與您聯繫。',
    'quote.modal.title': '已送出報價需求',
    'quote.modal.ok': '確定',

    'quote.contact.pre': '只是想聯絡我們嗎？',
    'quote.contact.link': '聯絡團隊',
    'quote.contact.post': '。',
  },
  'zh-Hans': {
    'quote.head.title': '获取报价 — Geo4Logistics',
    'quote.head.description':
      '向 Geo4Logistics 索取海运或空运报价。请告诉我们运输方式、起运地、目的地与货物 — 我们将回复逐项列明的价格与运输时间，通常在一到两个工作日内回复。',

    'quote.h1': '获取报价',
    'quote.intro':
      '请告诉我们运输方式、路线与货物。我们将回复逐项列明的价格与运输时间 — 通常在一到两个工作日内回复。',

    'quote.mode.legend': '运输方式',
    'quote.mode.oceanFcl': '海运整箱（FCL）',
    'quote.mode.oceanLcl': '海运拼箱（LCL）',
    'quote.mode.air': '空运',
    'quote.mode.rail': '铁路运输',
    'quote.mode.road': '公路与报关',
    'quote.mode.notSure': '尚未确定',

    'quote.field.origin': '起运地 *',
    'quote.field.origin.placeholder': '城市或港口、国家',
    'quote.field.destination': '目的地 *',
    'quote.field.destination.placeholder': '城市或港口、国家',
    'quote.field.name': '姓名 *',
    'quote.field.name.placeholder': '您的姓名',
    'quote.field.company': '公司 *',
    'quote.field.company.placeholder': '公司名称',
    'quote.field.email': '公司电子邮箱 *',
    'quote.field.email.placeholder': 'you@company.com',
    'quote.field.phone': '电话',
    'quote.field.phone.placeholder': '+1 / +886 …',
    'quote.field.details': '货件明细',
    'quote.field.details.placeholder':
      '您要运送什么货物？大致重量或体积、备货日期、截止时间，以及任何我们需要了解的信息…',

    'quote.submit': '提交报价需求',
    'quote.reply_note': '我们通常在一到两个工作日内以英文或中文回复。',
    'quote.error': '请检查您填写的信息后重试。',
    'quote.success': '感谢您！我们已收到您的需求，团队将尽快审阅并与您联系。',
    'quote.modal.title': '已提交报价需求',
    'quote.modal.ok': '确定',

    'quote.contact.pre': '只是想联系我们吗？',
    'quote.contact.link': '联系团队',
    'quote.contact.post': '。',
  },
}
